// @ts-strict-ignore
import {Directive, EventEmitter, Injector, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, output} from "@angular/core";
import {EventEmitterService} from "@event-emitter";
import {
	SimEnumExtendedService,
	SimMaterialDialogService,
	NotificationService,
	SimSystemExtendedService,
	SimTrainingRoomService,
	SimUtilityService,
	SystemHub,
} from "@services";
import {
	ClientProperties,
	ITrainingRoomWhitelist,
	PlayerMapping,
	RoomCapability,
	Tag,
	TrainingRoom,
	TrainingRoomGroup,
	TrainingRoomType,
} from "@models";
import {TranslateService} from "@ngx-translate/core";
import Enumerable from "linq";
import {Translations} from "@translations";

// Local wrappers extending the original room & roomGroup models
type RoomWrapper = TrainingRoom & {selectedPlayer?: PlayerMapping};
type RoomGroupWrapper = TrainingRoomGroup & {
	trainingRooms?: RoomWrapper[];
};

@Directive()
export abstract class TrainingRoomNavigationPanelBase implements OnInit, OnDestroy, OnChanges {
	T = Translations;

	// Inputs
	@Input() public isDebriefing = false;
	@Input() public hasPlayersAssigned = false;
	@Input() public isMultiplePlayerSelection = false;
	@Input() allowSelectionWhenOccupied = false;
	@Input() roomCapability: RoomCapability;
	@Input() roomType: TrainingRoomType;
	@Input() public currentTrainingRoom: TrainingRoom = null;
	@Input() public currentPlayer: PlayerMapping = null;
	public onTrainingRoomClicked = output<TrainingRoom>();
	@Input() public hideOsce = false;
	@Input({required: true}) trainingRoomWhitelist: ITrainingRoomWhitelist = null;
	// Properties
	public loadingTrainingRooms: boolean = false;
	public rawRooms: RoomWrapper[] = null;
	protected _simTrainingRoomService: SimTrainingRoomService;
	protected _systemHub: SystemHub;
	protected _simNotificationService: NotificationService;
	protected _simDialogService: SimMaterialDialogService;
	protected _emitterService: EventEmitterService;
	protected translate: TranslateService;
	protected _simExtendedEnumService: SimEnumExtendedService;
	public rawRoomGroups: RoomGroupWrapper[] = [];
	public filteredRoomGroups: RoomGroupWrapper[] = [];
	public filteredRooms: RoomWrapper[] = [];
	public loadingTrainingRoomGroups = true;
	public players: PlayerMapping[] = null;
	// Services
	protected _simSystemService: SimSystemExtendedService;
	private roomTags: Tag[] = [];
	@Input() inputFilterRoomIds: string[] = [];
	protected _clientProperties: ClientProperties;

	protected constructor(protected _injector: Injector) {
		this._simSystemService = this._injector.get(SimSystemExtendedService);
		this._simExtendedEnumService = this._injector.get(SimEnumExtendedService);
		this._simTrainingRoomService = this._injector.get(SimTrainingRoomService);
		this._systemHub = this._injector.get(SystemHub);
		this._simNotificationService = this._injector.get(NotificationService);
		this._simDialogService = this._injector.get(SimMaterialDialogService);
		this._emitterService = this._injector.get(EventEmitterService);
		this.translate = this._injector.get(TranslateService);

		this._simSystemService.getClientProperties().then((data) => {
			this._clientProperties = data;
		});
	}

	public async ngOnInit() {
		this.hideOsce = this.hideOsce || false;

		await this.loadTrainingRooms();

		this._emitterService.onPlayerStateChanged.subscribe((args) => {
			const foundPlayer = Enumerable.from(this.players).firstOrDefault((player) => {
				return player.id === args.player.id;
			});

			const foundTrainingRooms = Enumerable.from(this.rawRooms)
				.where((trainingRoom) => {
					return (trainingRoom.allPlayerDeviceIds || []).indexOf(args.player.id) >= 0;
				})
				.toArray();

			if (foundPlayer) {
				SimUtilityService.copy(args.player, foundPlayer);
			}

			if (foundTrainingRooms && foundTrainingRooms.length > 0) {
				for (const foundTrainingRoom of foundTrainingRooms) {
					(<any>foundTrainingRoom).selectedPlayer = args.player;
				}
			}
		});

		this._emitterService.onTrainingRoomUpdate.subscribe((args) => {
			const foundTrainingRoom = Enumerable.from(this.rawRooms || []).firstOrDefault(
				(trainingRoom) => trainingRoom.id === args.trainingRoom.id,
			);

			if (foundTrainingRoom) {
				Object.assign(foundTrainingRoom, args.trainingRoom);
			}
		});
	}

	public async fireTrainingRoomClicked(trainingRoom: TrainingRoom, checked: boolean) {
		// Checking if room is checked or not
		// in case of room planner when room gets deselected
		if (!checked) {
			await this.selectTrainingRoom(trainingRoom);
		} else {
			await this.unselectTrainingRoom(trainingRoom);
		}
	}

	private async selectTrainingRoom(trainingRoom: TrainingRoom) {
		let success = true;
		if (!this.allowSelectionWhenOccupied) {
			success = await this._simTrainingRoomService
				.selectTrainingRoom({
					trainingRoomId: trainingRoom.id,
				})
				.catch((_) => false);
		}

		//SIMSOFT-8273 - current workaround until selectTrainingRoom also works for tablets/debriefing
		if (success || true) {
			this.onTrainingRoomClicked.emit(trainingRoom);
		}
	}

	private async unselectTrainingRoom(trainingRoom: TrainingRoom) {
		if (!this.allowSelectionWhenOccupied) {
			await this._simTrainingRoomService.freeOccupiedTrainingRoom({
				trainingRoomId: trainingRoom.id,
			});
		}

		//so this is a very weird condition:
		//isMultiplePlayerSelection is currently ONLY set in live-view-selection
		//but there we need it so the onTrainingRoomClicked -> selectTrainingRoom is handled correctly (need a trainingRoom to deselect)
		this.onTrainingRoomClicked.emit(!this.isMultiplePlayerSelection ? null : trainingRoom);
	}

	ngOnDestroy(): void {}

	async ngOnChanges(changes: SimpleChanges) {
		if (
			(changes["inputFilterRoomIds"] && changes["inputFilterRoomIds"].previousValue !== changes["inputFilterRoomIds"].currentValue) ||
			(changes["trainingRoomWhitelist"] &&
				changes["trainingRoomWhitelist"].previousValue !== changes["trainingRoomWhitelist"].currentValue)
		) {
			await this.initializeTrainingRooms();
		}
	}

	isOtherTrainingRoomGroupAndHasOnlyVirtualAndTTTRooms(
		trainingRoomGroup: TrainingRoomGroup & {
			trainingRooms?: RoomWrapper[];
		},
	) {
		if (trainingRoomGroup.id === null) {
			return trainingRoomGroup.trainingRooms.some((room) => {
				return (
					room.type !== TrainingRoomType.Virtual &&
					!this._simExtendedEnumService.containsFlags(room.roomCapabilities, RoomCapability.TrainTheTrainer)
				);
			});
		} else {
			return true;
		}
	}

	hasVirtualRooms() {
		return this.filteredRooms?.some((room) => room.type === TrainingRoomType.Virtual);
	}

	hasTTTRooms() {
		return this.filteredRooms?.some((room) =>
			this._simExtendedEnumService.containsFlags(room.roomCapabilities, RoomCapability.TrainTheTrainer),
		);
	}

	isTrainTheTrainerRoom(trainingRoom: TrainingRoom & {selectedPlayer?: PlayerMapping}) {
		return this._simExtendedEnumService.containsFlags(trainingRoom.roomCapabilities, RoomCapability.TrainTheTrainer);
	}

	public removeRoomTag(removeTag: Tag): void {
		this.roomTags = this.roomTags.filter((tag) => tag.id !== removeTag.id);
		this.filterRoomsByTags();
	}

	public addRoomTag(addTag: Tag): void {
		this.roomTags.push(addTag);
		this.filterRoomsByTags();
	}

	protected async loadTrainingRooms() {
		this.loadingTrainingRooms = true;
		await this.initializeTrainingRooms();
		await this.initializePlayers();
		setTimeout(() => {
			this.loadingTrainingRooms = false;
		}, 1000);
	}

	private async initializePlayers() {
		this.players = await this._simSystemService.getDebriefingPlayers().catch(() => (this.players = null));
		this.mapPlayersToTrainingRooms();
	}

	private async initializeTrainingRooms() {
		let trainingRoomResult = await this._simTrainingRoomService
			.getTrainingRoomsWithFilter(this.roomType | 0, this.roomCapability | 0)
			.catch();
		this.rawRooms = trainingRoomResult || [];

		// See: SIMSOFT-4312
		if (this.hideOsce) {
			this.rawRooms = Enumerable.from(this.rawRooms)
				.where((trainingRoom) => {
					return trainingRoom.type !== TrainingRoomType.Virtual && trainingRoom.type !== TrainingRoomType.DigitalSignage;
				})
				.toArray();
		}

		// ------------------------- //
		// Filter training rooms     //
		// ------------------------- //
		if (this.rawRooms.length && this.inputFilterRoomIds?.length) {
			this.rawRooms = Enumerable.from(this.rawRooms)
				.where((trainingRoom) => !this.inputFilterRoomIds.includes(trainingRoom.id))
				.toArray();
		}

		if (this.trainingRoomWhitelist) {
			if (this.trainingRoomWhitelist.trainingRoomIds?.length) {
				this.rawRooms = Enumerable.from(this.rawRooms)
					.where((trainingRoom) => this.trainingRoomWhitelist.trainingRoomIds?.includes(trainingRoom.id))
					.toArray();
			}
			if (this.trainingRoomWhitelist.trainingRoomGroupIds?.length) {
				this.rawRooms = Enumerable.from(this.rawRooms)
					.where((trainingRoom) => this.trainingRoomWhitelist.trainingRoomGroupIds?.includes(trainingRoom.trainingRoomGroupId))
					.toArray();
			}
		}

		await this.loadTrainingRoomGroups();
		this.rawRooms.sort();
		this.filteredRooms = this.rawRooms;
	}

	private mapPlayersToTrainingRooms() {
		if (this.players && this.players.length > 0 && this.rawRooms && this.rawRooms.length > 0) {
			for (const trainingRoom of this.rawRooms) {
				trainingRoom.selectedPlayer = Enumerable.from(this.players).firstOrDefault((player) => {
					return (trainingRoom.allPlayerDeviceIds || []).indexOf(player.id) >= 0;
				});
			}
		}
	}

	private loadTrainingRoomGroups() {
		// Extract groups
		this.loadingTrainingRoomGroups = true;
		return this._simTrainingRoomService
			.getTrainingRoomGroups()
			.then((loadTrainingRoomGroupsResult) => {
				if (!loadTrainingRoomGroupsResult) {
					return;
				}

				this.rawRoomGroups = loadTrainingRoomGroupsResult || [];

				// See: SIMSOFT-4313
				const otherTrainingRoomGroup: TrainingRoomGroup = <any>(<Partial<TrainingRoomGroup>>{
					id: null,
					name: this.translate.instant(this.T.TRAINING_ROOM_PANEL_OTHER_TRAINING_ROOMS),
				});
				otherTrainingRoomGroup["trainingRooms"] = [];
				this.rawRoomGroups.push(otherTrainingRoomGroup);

				if (this.rawRoomGroups?.length) {
					this.assignRoomsToGroups(this.rawRoomGroups, this.rawRooms);
				} else {
					this.rawRoomGroups.push(<any>{
						name: this.translate.instant(this.T.TRAINING_ROOM_PANEL_TRAINING_ROOMS),
						trainingRooms: this.rawRooms,
					});
				}

				this.rawRoomGroups = this.rawRoomGroups.filter((group) => group.trainingRooms?.length > 0);
				this.filteredRoomGroups = this.rawRoomGroups;
			})
			.finally(() => {
				this.loadingTrainingRoomGroups = false;
			});
	}

	private assignRoomsToGroups(roomGroups: RoomGroupWrapper[], trainingRooms: RoomWrapper[]) {
		for (const trainingRoomGroup of roomGroups) {
			trainingRoomGroup.trainingRooms = Enumerable.from(trainingRooms)
				.where((trainingRoom) => {
					return !this.isTrainTheTrainerRoom(trainingRoom);
				})
				.where((trainingRoom) => {
					return (
						trainingRoom.trainingRoomGroupId === trainingRoomGroup.id ||
						(!trainingRoom.trainingRoomGroupId && !trainingRoomGroup.id)
					);
				})
				.toArray()
				.sort(this.sortTrainingRooms);
		}
	}

	private sortTrainingRooms(roomA: TrainingRoom, roomB: TrainingRoom) {
		if (roomA.order < roomB.order) {
			return -1;
		} else if (roomA.order > roomB.order) {
			return 1;
		} else {
			return roomA.name.localeCompare(roomB.name);
		}
	}

	private filterRoomsByTags(): void {
		this.filteredRooms = !!this.roomTags.length
			? this.rawRooms.filter((room) => room.tagIds?.some((tagId) => this.roomTags.some((tag) => tag.id === tagId)))
			: this.rawRooms;
		this.assignRoomsToGroups(this.filteredRoomGroups, this.filteredRooms);
	}
}
